import type {
  GetBudgetExceededQuery,
  Project,
  ProjectExpenseStats,
  ProjectOurWorkStats,
} from "@src/__generated__/graphql";
import { ProjectTypeEnum } from "@src/__generated__/urql-graphql";
import { map } from "lodash";
import { makeObservable, observable } from "mobx";
import type { DeepPick } from "ts-essentials";

type TProjects = NonNullable<GetBudgetExceededQuery["projects"]>["data"][0];

export class ProjectModel {
  id: Project["id"];
  title: Project["title"];
  brand: {
    id: string;
    name: Project["brand"]["name"];
    client: {
      image?: DeepPick<
        Project["brand"]["client"]["image"],
        {
          urls: {
            thumbnail: never;
          };
        }
      >;
    };
  };
  billable: Project["billable"];

  projectManager: TProjects["projectManager"];
  budgetExceeded: NonNullable<Project["stats"]>["budget_exceeded"];
  code: Project["code"];
  ourWork: OurWorkModel[];
  expenses: ExpenseModel[];
  @observable isOpen = false;
  __subRows: (OurWorkModel | ExpenseModel)[];

  constructor(src: TProjects) {
    makeObservable(this);
    this.id = src.id;
    this.title = src.title;
    this.brand = src.brand;
    this.billable = src.billable;
    this.code = src.code;
    this.projectManager = src.projectManager;
    this.budgetExceeded = src.stats?.budget_exceeded ?? 0;
    this.ourWork = map(
      src.ourWork,
      (w) =>
        new OurWorkModel({
          ...w,
          billable: src.billable,
          project_id: src.id,
          is_time_and_materials: src.type === ProjectTypeEnum.TimeMaterials,
        }),
    );
    this.expenses = map(
      src.expenses,
      (e) => new ExpenseModel({ ...e, project_id: src.id }),
    );
    this.__subRows = [...this.ourWork, ...this.expenses];
  }

  static fromGetBudgetExceededQuery(src: GetBudgetExceededQuery["projects"]) {
    return (
      src?.data.map((i) => {
        const {
          id,
          brand,
          title,
          code,
          expenses,
          ourWork,
          stats,
          projectManager,
          billable,
          type,
        } = i;

        return new ProjectModel({
          id,
          billable,
          type,
          brand,
          title,
          code,
          expenses,
          ourWork,
          stats,
          projectManager,
        });
      }) ?? []
    );
  }
}

export class OurWorkModel {
  id: string;
  title: string;
  billable: Project["billable"];
  is_time_and_materials: boolean;

  project_id: string;
  credit: ProjectOurWorkStats["credit"];
  spent_budget: ProjectOurWorkStats["spent_budget"];
  budget_exceeded: ProjectOurWorkStats["budget_exceeded"];
  total_budget: ProjectOurWorkStats["total_budget"];
  spent_budget_percent: ProjectOurWorkStats["spent_budget_percent"];

  spent_time: ProjectOurWorkStats["spent_time"];
  invoiced_percent: ProjectOurWorkStats["invoiced_percent"];
  remaining_budget: ProjectOurWorkStats["remaining_budget"];
  remaining_time: ProjectOurWorkStats["remaining_time"];

  constructor(
    src: TProjects["ourWork"][0] & {
      billable: Project["billable"];
      project_id: string;
      is_time_and_materials: boolean;
    },
  ) {
    this.id = src.id;
    this.billable = src.billable;
    this.is_time_and_materials = src.is_time_and_materials;
    this.title = src.title;
    this.project_id = src.project_id;
    this.credit = src.stats.credit;
    this.spent_budget = src.stats.spent_budget;
    this.budget_exceeded = src.stats.budget_exceeded;
    this.total_budget = src.stats.total_budget;
    this.spent_budget_percent = src.stats.spent_budget_percent;
    this.spent_time = src.stats.spent_time;
    this.invoiced_percent = src.stats.invoiced_percent;
    this.remaining_budget = src.stats.remaining_budget;
    this.remaining_time = src.stats.remaining_time;
  }
}

export class ExpenseModel {
  id: string;
  title: string;
  project_id: string;
  planned_expenses: ProjectExpenseStats["planned_expenses"];
  real_expenses: ProjectExpenseStats["real_expenses"];
  planned_commission: ProjectExpenseStats["planned_commission"];
  credit: ProjectExpenseStats["credit"];

  budget_exceeded: ProjectExpenseStats["budget_exceeded"];
  total_budget: ProjectExpenseStats["total_budget"];
  real_expenses_percent: ProjectExpenseStats["real_expenses_percent"];
  stats: TProjects["expenses"][0]["stats"];

  constructor(src: TProjects["expenses"][0] & { project_id: string }) {
    this.id = src.id;
    this.title = src.title;
    this.project_id = src.project_id;
    this.planned_expenses = src.stats.planned_expenses;
    this.real_expenses = src.stats.real_expenses;
    this.planned_commission = src.stats.planned_commission;
    this.credit = src.stats.credit;
    this.budget_exceeded = src.stats.budget_exceeded;
    this.total_budget = src.stats.total_budget;
    this.real_expenses_percent = src.stats.real_expenses_percent;
    this.stats = src.stats;
  }
}
